import { useEffect, useRef, useState, type RefObject } from 'react'

export interface GanttScrollState {
  scrollLeft: number
  scrollTop: number
  viewportWidth: number
  viewportHeight: number
}

const SAME = (a: GanttScrollState, b: GanttScrollState) =>
  a.scrollLeft === b.scrollLeft && a.scrollTop === b.scrollTop && a.viewportWidth === b.viewportWidth && a.viewportHeight === b.viewportHeight

/**
 * Scroll offsets and viewport size of the single Gantt scroller, rAF-throttled.
 * Deliberately component state, never the store: it changes on every frame of a
 * scroll and only the timeline layers depend on it.
 */
export const useGanttScroll = (scrollerRef: RefObject<HTMLDivElement>): GanttScrollState => {
  const [state, setState] = useState<GanttScrollState>({ scrollLeft: 0, scrollTop: 0, viewportWidth: 0, viewportHeight: 0 })
  const frame = useRef<number | null>(null)

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined
    const read = () => {
      frame.current = null
      const next = { scrollLeft: el.scrollLeft, scrollTop: el.scrollTop, viewportWidth: el.clientWidth, viewportHeight: el.clientHeight }
      setState((prev) => (SAME(prev, next) ? prev : next))
    }
    const onScroll = () => { if (frame.current == null) frame.current = requestAnimationFrame(read) }
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = new ResizeObserver(onScroll)
    ro.observe(el)
    read()
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro.disconnect()
      if (frame.current != null) cancelAnimationFrame(frame.current)
    }
  }, [scrollerRef])

  return state
}
